import Phaser from 'phaser';
import {
  ASSET_KEYS,
  COLORS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  HISTORIKK_GRAF_DAGER,
  LAV_KAPITAL_GRENSE,
  SJANGER_FALLBACK_FARGE,
  SJANGER_FARGER,
  fontStr,
} from '@/config';
import { tegnPennGlyph } from '@/ui/Glyphs';
import { gameState } from '@/state/store';
import { snittTerninger } from '@/systems/tier';
import { abonnerAdmin } from '@/state/adminMode';
import {
  DAGER_PER_AAR,
  SESONG_BESKRIVELSE,
  SESONG_NAVN,
  dagIAaret,
  sesongForDag,
  type Sesong,
} from '@/systems/sesong';
import { KATEGORI_NAVN } from '@/systems/brage';
import { PRIS_KATALOG, type PrisId } from '@/systems/priser';
import type { FilmRettighet } from '@/systems/lydbokOgFilm';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { Kolonne } from '@/ui/layout';

const PANEL_WIDTH = 360;

type PrisArkivFilter = 'alle' | 'brage' | PrisId;

export type DireksjonDashboardCallbacks = {
  onAapneMinKarriere?: () => void;
  onAapneAdmin?: () => void;
};

export class DireksjonDashboard {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private unsubscribeAdmin?: () => void;
  private scrollY = 0;
  private scrollMax = 0;
  private innholdsContainer?: Phaser.GameObjects.Container;
  private mask?: Phaser.Display.Masks.GeometryMask;
  private wheelHandler?: (_p: unknown, _g: unknown, _dx: number, dy: number) => void;
  private prisFilter: PrisArkivFilter = 'alle';

  constructor(
    private scene: Phaser.Scene,
    private callbacks: DireksjonDashboardCallbacks = {},
  ) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(): void {
    if (this.layer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    this.layer = this.scene.add.container(0, 0).setDepth(15);

    const bg = this.scene.add
      .rectangle(0, 0, roomWidth, GAME_HEIGHT, COLORS.beige, 0.92)
      .setOrigin(0, 0);
    this.layer.add(bg);

    this.wheelHandler = (
      _p: unknown,
      _g: unknown,
      _dx: number,
      dy: number,
    ) => {
      this.scrollBy(dy);
    };
    this.scene.input.on('wheel', this.wheelHandler);

    bg.setInteractive();
    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const startY = pointer.y;
      const startScroll = this.scrollY;
      const onMove = (p: Phaser.Input.Pointer) => {
        const dy = p.y - startY;
        this.scrollY = Math.max(0, Math.min(this.scrollMax, startScroll - dy));
        if (this.innholdsContainer) this.innholdsContainer.y = -this.scrollY;
      };
      const onUp = () => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
      };
      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });

    this.render();
    this.unsubscribe = gameState.subscribe(() => this.render());
    this.unsubscribeAdmin = abonnerAdmin(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.unsubscribeAdmin?.();
    this.unsubscribeAdmin = undefined;
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdsContainer = undefined;
    this.mask = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
  }

  private scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    if (this.innholdsContainer) this.innholdsContainer.y = -this.scrollY;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const padding = 40;
    const innholdsBredde = roomWidth - padding * 2;

    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, 0, roomWidth, GAME_HEIGHT);
    this.mask = maskShape.createGeometryMask();

    const innhold = this.scene.add.container(padding, 0);
    innhold.setMask(this.mask);
    this.layer.add(innhold);
    this.innholdsContainer = innhold;

    // Runde 48: start under HUD-zonen (96px + skille) så «Direksjon»-
    // tittelen og toppen av oversikten ikke ligger bak HUD-en.
    let y = 110;

    y = this.renderHeader(innhold, innholdsBredde, y);
    y += 12;

    const snap = gameState.snapshot;
    if (snap.kroner < LAV_KAPITAL_GRENSE) {
      y = this.renderRoedt(innhold, innholdsBredde, y, snap.kroner);
      y += 16;
    }

    y = this.renderKpiCards(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderMinKarriere(innhold, innholdsBredde, y);
    y += 24;

    if (gameState.harDashboardSesongPrognose()) {
      y = this.renderSesongPrognose(innhold, innholdsBredde, y);
      y += 24;
    }

    y = this.renderGraf(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderTopp10(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderSjangerFordeling(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderPrisArkiv(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderInternasjonaleAvtaler(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderLydbokOgFilm(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderCalidrisAvkortninger(innhold, innholdsBredde, y);
    y += 40;

    // Runde 48: Lyd/App/Innstillinger flyttet til eget Settings-panel
    // (tannhjul i HUD). Dashboardet er nå ren forretnings-oversikt.

    const synligHoyde = GAME_HEIGHT - 60;
    this.scrollMax = Math.max(0, y - synligHoyde);
  }

  private renderMinKarriere(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const sf = gameState.spillerForfatter;
    const snitt = beregnStatSnitt(sf.stats);
    const maal = 30;

    const overskrift = this.scene.add
      .text(0, y, 'MIN KARRIERE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 38;

    // Layout-primitiv (2026-06-04): navn (kan wrappe), meta og badge/tips
    // måles og stables fra faktisk høyde i stedet for de hardkodede
    // y+16/68/110. Navnet wrapper på width-296 og kolliderte før med
    // meta-linjen. Vi måler teksten FØRST, regner kortH fra innholdet
    // (minst 168 som før), og lager så skygge/bg/border/ikon/knapp som
    // dermed alltid rommer teksten. Navnet starter ved x=72 (ved ikonet);
    // meta/tips ligger på x=20 som før.
    const fornavn = (sf.navn.trim() || 'Sven').split(' ')[0];
    const navn = this.scene.add
      .text(72, 0, `${fornavn} – aspirerende forfatter`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#862736',
        // Runde 48: smalere wrap så navnet ikke løper inn under «Åpne»-knappen.
        wordWrap: { width: width - 324 },
      })
      .setOrigin(0, 0);

    const meta = this.scene.add
      .text(
        20,
        0,
        `Stat-snitt ${snitt.toFixed(1)} · Skrivelab-bidrag ${sf.skrivelabBidrag} / ${maal}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width: width - 272 },
        },
      )
      .setOrigin(0, 0);

    const sisteLinje = sf.publiseringsklarErklart
      ? this.scene.add
          .text(20, 0, '✓ Publiseringsklar — runde 28 åpner skrive-modulen', {
            fontFamily: FONTS.hud,
            fontSize: fontStr(14),
            color: '#3d5a4d',
            fontStyle: '700',
            wordWrap: { width: width - 272 },
          })
          .setOrigin(0, 0)
      : this.scene.add
          .text(20, 0, 'Send tekster i Skrivelab for å bygge stats.', {
            fontFamily: FONTS.body,
            fontSize: fontStr(14),
            color: '#7a7060',
            fontStyle: 'italic',
            wordWrap: { width: width - 272 },
          })
          .setOrigin(0, 0);

    // Stable teksten fra et indre topp-padding (16) og regn kortH slik at
    // den rommer både teksten og den vertikalt sentrerte knappen til høyre.
    // (`.height` er gyldig så snart teksten er opprettet — vi trenger ikke
    // legge den i layeret før vi måler.)
    const knappH = 66;
    const tekstTopp = 16;
    const tekstKol = new Kolonne(0, y + tekstTopp, 10);
    tekstKol.legg(navn);
    tekstKol.legg(meta);
    tekstKol.legg(sisteLinje);
    const kortH = Math.max(
      168,
      tekstKol.hoyde() + tekstTopp * 2,
      knappH + 32,
    );

    // Skygge/bg/border opprettes og legges FØRST (bakerst), så ikon, tekst
    // og knapp ligger oppå.
    const shadow = lagRundetRekt(this.scene, 3, y + 5, width, kortH, 0x000000, 0.22, 8);
    layer.add(shadow);
    const bg = lagRundetRekt(this.scene, 0, y, width, kortH, COLORS.white, 1, 8).setStrokeStyle(
      2,
      COLORS.wine,
      0.4,
    );
    layer.add(bg);
    const venstreBorder = lagRundetRekt(this.scene, 0, y, 4, kortH, COLORS.wine, 1, 2);
    layer.add(venstreBorder);

    // Runde 45: ikon-illustrasjon byttes fra ✍️-emoji til ekte skrivelab-
    // WebP-ikon (med Phaser-tegnet penn som fallback). Konsistent med
    // resten av FAB/etasje-ikonene som ble byttet i runde 39/44.
    if (this.scene.textures.exists(ASSET_KEYS.iconFabSkrivelab)) {
      const img = this.scene.add
        .image(40, y + 32, ASSET_KEYS.iconFabSkrivelab)
        .setOrigin(0.5);
      const tex = this.scene.textures
        .get(ASSET_KEYS.iconFabSkrivelab)
        .getSourceImage();
      const tw = (tex as HTMLImageElement).width || 1;
      const th = (tex as HTMLImageElement).height || 1;
      const target = 36;
      img.setScale(Math.min(target / tw, target / th));
      layer.add(img);
    } else {
      const ikon = tegnPennGlyph(this.scene, 40, y + 32, 0x862736);
      ikon.setScale(1.6);
      layer.add(ikon);
    }

    // Den målte/stablede teksten legges oppå bg/ikon.
    layer.add([navn, meta, sisteLinje]);

    const knappW = 220;
    const knappX = width - knappW - 16;
    // Runde 48 (Sven): knappen flyttet opp til topp-høyre, på linje med
    // navnet, i stedet for vertikalt sentrert der den lå oppå meta-teksten.
    const knappY = y + 16;
    const knappShadow = lagRundetRekt(
      this.scene,
      knappX + 2,
      knappY + 4,
      knappW,
      knappH,
      0x000000,
      0.28,
      8,
    );
    const knappBg = lagRundetRekt(
      this.scene,
      knappX,
      knappY,
      knappW,
      knappH,
      COLORS.ochre,
      1,
      8,
    ).setStrokeStyle(2, COLORS.wine, 0.6);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Åpne →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add([knappShadow, knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => {
      this.callbacks.onAapneMinKarriere?.();
    });

    return y + kortH;
  }

  private renderHeader(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    // Layout-primitiv (2026-06-04): tittel og undertittel stables fra målt
    // høyde i stedet for det hardkodede y+78 / y+120, så de ikke kolliderer
    // når display-fonten blåses opp.
    const tittel = this.scene.add
      .text(0, 0, 'Direksjon', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#862736',
      })
      .setOrigin(0, 0);

    const undertittel = this.scene.add
      .text(0, 0, 'Helhetsbildet av forlaget', {
        fontFamily: FONTS.body,
        fontSize: fontStr(24),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);

    const kol = new Kolonne(0, y, 14);
    kol.legg(tittel);
    kol.legg(undertittel);
    layer.add([tittel, undertittel]);
    void width;
    return kol.bunn();
  }

  private renderRoedt(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
    kroner: number,
  ): number {
    // Layout-primitiv (2026-06-04): boks-høyden regnes fra målt varseltekst
    // (kan wrappe på width-48) i stedet for fast 112px, så lange beløp/tekster
    // ikke flyter utenfor den vinrøde boksen. Teksten måles først, bg lages og
    // legges bakerst, teksten sentreres vertikalt i den målte boksen.
    const tekst = this.scene.add
      .text(
        24,
        0,
        `⚠ Lav kapital: ${kroner.toLocaleString('nb-NO')} kr — vurder å selge backlist eller pause kampanjer`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#ffffff',
          fontStyle: '700',
          wordWrap: { width: width - 48 },
        },
      )
      .setOrigin(0, 0.5);

    const h = Math.max(112, tekst.height + 40);
    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.wine, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.3);
    layer.add(bg);

    tekst.setPosition(24, y + h / 2);
    layer.add(tekst);
    return y + h;
  }

  private renderSesongPrognose(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const dag = gameState.dag;
    const naa = sesongForDag(dag);
    const neste = nesteSesong(dag);
    const dagerTilNeste = dagerTilSesongSkift(dag);

    // Layout-primitiv (2026-06-04): overskrift og naa-tekst (kan wrappe på
    // width-40) stables fra målt høyde, boks-høyden regnes så den rommer
    // innholdet pluss den bunn-forankrede neste-linjen. Erstatter de
    // hardkodede y+14/58 og fast 200px som sprakk når sesong-beskrivelsen
    // wrappet. Tekstene måles først, bg lages og legges bakerst.
    const overskrift = this.scene.add
      .text(20, 0, 'SESONG-PROGNOSE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);

    const naaText = this.scene.add
      .text(
        20,
        0,
        `${SESONG_NAVN[naa]} (nå) — ${SESONG_BESKRIVELSE[naa]}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          wordWrap: { width: width - 40 },
        },
      )
      .setOrigin(0, 0);

    const nesteText = this.scene.add
      .text(
        20,
        0,
        `Neste: ${SESONG_NAVN[neste]} om ${dagerTilNeste} ${dagerTilNeste === 1 ? 'dag' : 'dager'}`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#862736',
          fontStyle: '700',
        },
      )
      .setOrigin(0, 0);

    const toppKol = new Kolonne(20, y + 14, 14);
    toppKol.legg(overskrift);
    toppKol.legg(naaText);
    const h = Math.max(200, (toppKol.bunn() - y) + 16 + nesteText.height + 24);
    nesteText.setPosition(20, y + h - 24 - nesteText.height);

    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ochre, 0.8);
    layer.add(bg);

    layer.add(overskrift);
    layer.add(naaText);
    layer.add(nesteText);

    return y + h;
  }

  private renderKpiCards(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const totalInntekt = gameState.beregnTotalInntekt();
    const totalUtgift = gameState.beregnTotalUtgift();
    const netto = totalInntekt - totalUtgift;
    const utgivelser = gameState.antallUtgivelser();

    const gap = 16;
    const kortBredde = Math.floor((width - gap * 3) / 4);
    const kortHoyde = 200;

    const kort = [
      {
        label: 'Total inntekt',
        verdi: this.kompakt(totalInntekt),
        farge: '#8b5e3c',
      },
      {
        label: 'Total utgift',
        verdi: this.kompakt(totalUtgift),
        farge: '#862736',
      },
      {
        label: 'Netto',
        verdi: this.kompakt(netto),
        farge: netto >= 0 ? '#3d5a4d' : '#862736',
      },
      {
        label: 'Utgivelser',
        verdi: utgivelser.toLocaleString('nb-NO'),
        farge: '#1a1a1a',
      },
    ];

    for (let i = 0; i < kort.length; i += 1) {
      const x = i * (kortBredde + gap);
      const k = kort[i];

      const bg = this.scene.add
        .rectangle(x, y, kortBredde, kortHoyde, COLORS.white, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.3);
      layer.add(bg);

      const label = this.scene.add
        .text(x + 16, y + 18, k.label.toUpperCase(), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#8b5e3c',
          fontStyle: '700',
          wordWrap: { width: kortBredde - 32 },
        })
        .setOrigin(0, 0);
      layer.add(label);

      const verdi = this.scene.add
        .text(x + 16, y + kortHoyde - 26, k.verdi, {
          fontFamily: FONTS.display,
          fontSize: fontStr(46),
          color: k.farge,
          wordWrap: { width: kortBredde - 32 },
        })
        .setOrigin(0, 1);
      layer.add(verdi);
    }

    return y + kortHoyde;
  }

  private renderGraf(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const h = 320;
    const padding = 56;

    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    layer.add(bg);

    const tittel = this.scene.add
      .text(20, y + 16, `KRONER-NIVÅ — SISTE ${HISTORIKK_GRAF_DAGER} DAGER`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    const historikk = gameState.historikk;
    const snittsnitt = historikk.slice(-HISTORIKK_GRAF_DAGER);

    const grafX = padding;
    const grafY = y + 60;
    const grafW = width - padding - 20;
    const grafH = h - 100;

    const g = this.scene.add.graphics();
    g.lineStyle(1, COLORS.ochre, 0.25);
    for (let i = 0; i <= 4; i += 1) {
      const gy = grafY + (grafH * i) / 4;
      g.beginPath();
      g.moveTo(grafX, gy);
      g.lineTo(grafX + grafW, gy);
      g.strokePath();
    }
    layer.add(g);

    if (snittsnitt.length === 0) {
      const tekst = this.scene.add
        .text(width / 2, y + h / 2 + 20, 'Ingen historikk ennå — spill noen dager først', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#8b5e3c',
          fontStyle: 'italic',
        })
        .setOrigin(0.5);
      layer.add(tekst);
      return y + h;
    }

    const verdier = snittsnitt.map((s) => s.kroner);
    const maks = Math.max(...verdier, 1);
    const min = Math.min(...verdier, 0);
    const span = Math.max(1, maks - min);

    const linje = this.scene.add.graphics();
    linje.lineStyle(3, COLORS.wine, 1);
    linje.beginPath();
    snittsnitt.forEach((s, i) => {
      const px = grafX + (grafW * i) / Math.max(1, snittsnitt.length - 1);
      const py = grafY + grafH - ((s.kroner - min) / span) * grafH;
      if (i === 0) linje.moveTo(px, py);
      else linje.lineTo(px, py);
    });
    linje.strokePath();
    layer.add(linje);

    const aksLabelMaks = this.scene.add
      .text(grafX - 10, grafY, this.kompakt(maks), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    layer.add(aksLabelMaks);

    const aksLabelMin = this.scene.add
      .text(grafX - 10, grafY + grafH, this.kompakt(min), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 1);
    layer.add(aksLabelMin);

    const forste = snittsnitt[0];
    const siste = snittsnitt[snittsnitt.length - 1];
    const xStart = this.scene.add
      .text(grafX, grafY + grafH + 8, `dag ${forste.dag}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    layer.add(xStart);

    const xEnd = this.scene.add
      .text(grafX + grafW, grafY + grafH + 8, `dag ${siste.dag}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    layer.add(xEnd);

    return y + h;
  }

  private renderTopp10(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const topp = gameState.beregnTopp10Bestselgere();

    const tittel = this.scene.add
      .text(0, y, 'TOPP 10 BESTSELGERE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    if (topp.length === 0) {
      const tekst = this.scene.add
        .text(0, cy, 'Ingen utgitte bøker ennå', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tekst);
      return cy + 40;
    }

    // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt tittel +
    // forfatter i stedet for fast 92px. Boktitler wrapper på width-360 og
    // dyttet før forfatter-linjen (bunn-forankret) utenfor raden / oppå
    // naboraden. Vi måler tittel/forfatter, regner radHoyde, lager bg ETTER
    // måling, og akkumulerer radY manuelt.
    const RAD_GAP = 8;
    const TOPP_PAD = 12;
    let radY = cy;
    for (let i = 0; i < topp.length; i += 1) {
      const bok = topp[i];

      const tittelT = this.scene.add
        .text(80, 0, bok.title, {
          fontFamily: FONTS.display,
          fontSize: fontStr(24),
          color: '#1a1a1a',
          wordWrap: { width: width - 360 },
        })
        .setOrigin(0, 0);

      const forfatterT = this.scene.add
        .text(80, 0, bok.author, {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);

      // Radhøyde rommer tittel (kan wrappe) + forfatter med padding, men
      // minst 92px som før.
      const radHoyde = Math.max(
        92,
        TOPP_PAD + tittelT.height + 6 + forfatterT.height + TOPP_PAD,
      );

      tittelT.setPosition(80, radY + TOPP_PAD);
      forfatterT.setPosition(80, radY + radHoyde - TOPP_PAD - forfatterT.height);

      const bg = this.scene.add
        .rectangle(0, radY, width, radHoyde, 0xe9d8a8, 0.55)
        .setOrigin(0, 0);
      layer.add(bg);

      const venstreBorder = this.scene.add
        .rectangle(0, radY, 4, radHoyde, COLORS.wine, 1)
        .setOrigin(0, 0);
      layer.add(venstreBorder);

      const nrTekst = this.scene.add
        .text(20, radY + radHoyde / 2, `${i + 1}.`, {
          fontFamily: FONTS.display,
          fontSize: fontStr(28),
          color: '#862736',
        })
        .setOrigin(0, 0.5);
      layer.add(nrTekst);

      layer.add(tittelT);
      layer.add(forfatterT);

      const salgT = this.scene.add
        .text(width - 20, radY + TOPP_PAD, `${bok.salg.toLocaleString('nb-NO')} solgte`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      layer.add(salgT);

      const snitt = snittTerninger(bok.anmeldelser);
      const snittTxt = bok.anmeldelser.length > 0
        ? `${snitt.toFixed(1)} · ${bok.anmeldelser.length} anm.`
        : 'ingen anm.';
      const snittT = this.scene.add
        .text(width - 20, radY + radHoyde - TOPP_PAD, snittTxt, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#3a2e22',
        })
        .setOrigin(1, 1);
      layer.add(snittT);

      radY += radHoyde + RAD_GAP;
    }

    return radY - RAD_GAP;
  }

  private renderSjangerFordeling(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const fordeling = gameState.beregnSjangerFordeling();
    const sjangre = Object.keys(fordeling).sort(
      (a, b) => fordeling[b] - fordeling[a],
    );

    const tittel = this.scene.add
      .text(0, y, 'SJANGER-FORDELING', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    if (sjangre.length === 0) {
      const tekst = this.scene.add
        .text(0, cy, 'Ingen utgivelser å fordele', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tekst);
      return cy + 40;
    }

    const total = sjangre.reduce((s, k) => s + fordeling[k], 0);
    const radHoyde = 42;
    const labelBredde = 200;
    const tallBredde = 80;
    const barX = labelBredde + 16;
    const barMaksW = width - labelBredde - tallBredde - 32;

    for (let i = 0; i < sjangre.length; i += 1) {
      const sjanger = sjangre[i];
      const antall = fordeling[sjanger];
      const farge = SJANGER_FARGER[sjanger] ?? SJANGER_FALLBACK_FARGE;
      const radY = cy + i * radHoyde;

      const label = this.scene.add
        .text(0, radY + radHoyde / 2, sjanger, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0, 0.5);
      layer.add(label);

      // Runde 44: rounded sjanger-bars (8px) matcher pris-rader.
      const barBg = lagRundetRekt(
        this.scene,
        barX,
        radY + radHoyde / 2 - 9,
        barMaksW,
        18,
        COLORS.beigeDark,
        1,
        8,
      );
      layer.add(barBg);

      const frac = total > 0 ? antall / total : 0;
      const barW = Math.max(4, Math.round(barMaksW * frac));
      const bar = lagRundetRekt(
        this.scene,
        barX,
        radY + radHoyde / 2 - 9,
        barW,
        18,
        farge,
        1,
        8,
      );
      layer.add(bar);

      const antTxt = this.scene.add
        .text(width, radY + radHoyde / 2, `${antall}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(antTxt);
    }

    return cy + sjangre.length * radHoyde;
  }

  private renderPrisArkiv(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'PRIS-ARKIV', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    cy = this.renderPrisFilterPiller(layer, width, cy);
    cy += 16;

    type RadOppfoering = {
      kilde: 'brage' | PrisId;
      prisNavn: string;
      kategoriNavn: string;
      tittel: string;
      forfatterNavn: string;
      forlag: string;
      aar: number;
    };

    const rader: RadOppfoering[] = [];

    if (this.prisFilter === 'alle' || this.prisFilter === 'brage') {
      for (const v of gameState.brageHistorikk) {
        rader.push({
          kilde: 'brage',
          prisNavn: 'Brage',
          kategoriNavn: KATEGORI_NAVN[v.kategori],
          tittel: v.tittel,
          forfatterNavn: v.forfatterNavn,
          forlag: v.forlag,
          aar: 0,
        });
      }
    }

    for (const v of gameState.prisHistorikk) {
      if (this.prisFilter !== 'alle' && this.prisFilter !== v.prisId) continue;
      const pris = PRIS_KATALOG.find((p) => p.id === v.prisId);
      const kat = pris?.kategorier.find((k) => k.id === v.kategori);
      rader.push({
        kilde: v.prisId,
        prisNavn: pris?.navn ?? v.prisNavn,
        kategoriNavn: kat?.navn ?? v.kategori,
        tittel: v.bokTittel,
        forfatterNavn: v.forfatterNavn,
        forlag: v.forlag,
        aar: v.aar,
      });
    }

    if (rader.length === 0) {
      const tom = this.scene.add
        .text(
          0,
          cy,
          'Forlaget har ikke vunnet litterære priser ennå. Bygg gode bøker.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width },
          },
        )
        .setOrigin(0, 0);
      layer.add(tom);
      return cy + 40;
    }

    // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt prisLabel +
    // tittel + meta i stedet for fast 110px. Tittelen wrapper på width-320
    // og kolliderte før med den bunn-forankrede meta-linjen. Tekstene måles,
    // radH regnes, bg lages ETTER måling, og radY akkumuleres manuelt.
    const RAD_GAP = 8;
    const PAD = 10;
    let radY = cy;
    for (let i = 0; i < rader.length; i += 1) {
      const r = rader[i];
      const erSpiller = r.forlag === 'spiller';

      const prisLabel = this.scene.add
        .text(20, 0, r.prisNavn.toUpperCase(), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const tittelT = this.scene.add
        .text(20, 0, r.tittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: erSpiller ? '#862736' : '#1a1a1a',
          wordWrap: { width: width - 320 },
        })
        .setOrigin(0, 0);

      const forlagT = erSpiller ? 'DITT FORLAG' : r.forlag;
      const meta = this.scene.add
        .text(20, 0, `${r.forfatterNavn} · ${forlagT}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(13),
          color: erSpiller ? '#862736' : '#3a2e22',
          fontStyle: erSpiller ? '700' : 'italic',
        })
        .setOrigin(0, 0);

      // Stable prisLabel → tittel fra toppen; meta forankres til bunnen.
      const toppKol = new Kolonne(20, radY + PAD, 8);
      toppKol.legg(prisLabel);
      toppKol.legg(tittelT);
      const radH = Math.max(110, toppKol.hoyde() + 8 + meta.height + PAD * 2);
      meta.setPosition(20, radY + radH - PAD - meta.height);

      // Runde 44: rounded pris-rader (8px).
      const bg = lagRundetRekt(
        this.scene,
        0,
        radY,
        width,
        radH,
        erSpiller ? 0xb8860b : 0xe9d8a8,
        erSpiller ? 0.18 : 0.5,
        8,
      );
      layer.add(bg);

      const venstre = lagRundetRekt(
        this.scene,
        0,
        radY,
        6,
        radH,
        erSpiller ? 0xb8860b : 0xd9a14b,
        1,
        2,
      );
      layer.add(venstre);

      layer.add(prisLabel);
      layer.add(tittelT);
      layer.add(meta);

      const kat = this.scene.add
        .text(width - 16, radY + radH / 2, r.kategoriNavn, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(kat);

      radY += radH + RAD_GAP;
    }
    return radY - RAD_GAP;
  }

  private renderInternasjonaleAvtaler(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'INTERNASJONALE AVTALER', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    const avtaler = [...gameState.internasjonaleAvtaler];
    if (avtaler.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen oversettelses-avtaler ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      return cy + 40;
    }

    avtaler.sort((a, b) => {
      const totA = (a.engangsBelop ?? 0) + (a.totalRoyaltyMottatt ?? 0);
      const totB = (b.engangsBelop ?? 0) + (b.totalRoyaltyMottatt ?? 0);
      return totB - totA;
    });

    // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt tittel + meta
    // (begge kan wrappe) i stedet for fast 110px. Lang tittel/forfatter-linje
    // sprakk før raden. Venstre tekstkolonne stables fra toppen, meta
    // forankres til bunnen, og høyre kolonne (inntekt/undertekst) festes til
    // samme topp/bunn. radY akkumuleres manuelt.
    const RAD_GAP = 8;
    const PAD = 14;
    let radY = cy;
    for (let i = 0; i < avtaler.length; i += 1) {
      const a = avtaler[i];
      const totalInntekt = (a.engangsBelop ?? 0) + (a.totalRoyaltyMottatt ?? 0);
      const venstreTekstX = 76;

      const tittelT = this.scene.add
        .text(venstreTekstX, 0, a.tittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: '#862736',
          wordWrap: { width: width - 360 },
        })
        .setOrigin(0, 0);

      const forlagDel = a.forlagNavn ? `${a.forlagNavn} · ` : '';
      const meta = this.scene.add
        .text(
          venstreTekstX,
          0,
          `${forlagDel}${a.forfatterNavn} · ${a.sprak}`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(13),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width: width - 280 },
          },
        )
        .setOrigin(0, 0);

      // Tittel fra toppen, meta forankret til bunnen; radH minst 110px.
      tittelT.setPosition(venstreTekstX, radY + PAD);
      const radH = Math.max(110, PAD + tittelT.height + 8 + meta.height + PAD);
      meta.setPosition(venstreTekstX, radY + radH - PAD - meta.height);

      // Runde 44: rounded oversettelses-rader (8px).
      const bg = lagRundetRekt(
        this.scene,
        0,
        radY,
        width,
        radH,
        0xb8860b,
        0.14,
        8,
      );
      layer.add(bg);

      const venstre = lagRundetRekt(
        this.scene,
        0,
        radY,
        6,
        radH,
        0xb8860b,
        1,
        2,
      );
      layer.add(venstre);

      const flaggIkon = a.flagg ?? '↗';
      const flaggT = this.scene.add
        .text(20, radY + radH / 2, flaggIkon, {
          fontFamily: FONTS.display,
          fontSize: fontStr(28),
          color: '#1a1a1a',
        })
        .setOrigin(0, 0.5);
      layer.add(flaggT);

      layer.add(tittelT);
      layer.add(meta);

      const inntektT = this.scene.add
        .text(
          width - 16,
          radY + 12,
          `${totalInntekt.toLocaleString('nb-NO')} kr totalt`,
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(15),
            color: '#8b5e3c',
            fontStyle: '700',
          },
        )
        .setOrigin(1, 0);
      layer.add(inntektT);

      const undertekst: string[] = [];
      if (a.engangsBelop && a.engangsBelop > 0) {
        undertekst.push(`engangs ${a.engangsBelop.toLocaleString('nb-NO')}`);
      }
      if (a.royaltyPerSalg && a.royaltyPerSalg > 0) {
        undertekst.push(`${a.royaltyPerSalg} kr/salg`);
      }
      if (a.totalRoyaltyMottatt && a.totalRoyaltyMottatt > 0) {
        undertekst.push(
          `royalty ${a.totalRoyaltyMottatt.toLocaleString('nb-NO')}`,
        );
      }
      const undertekstLinje =
        undertekst.length > 0 ? undertekst.join(' · ') : `dag ${a.dag}`;
      const undertekstT = this.scene.add
        .text(width - 16, radY + radH - 12, undertekstLinje, {
          fontFamily: FONTS.body,
          fontSize: fontStr(12),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(1, 1);
      layer.add(undertekstT);

      radY += radH + RAD_GAP;
    }
    return radY - RAD_GAP;
  }

  private renderLydbokOgFilm(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'LYDBOK OG FILM', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#3d5a4d',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    const lydbok = gameState.lydbokAvtaler;
    const film = gameState.filmRettigheter;

    const undertittelLydbok = this.scene.add
      .text(0, cy, 'Aktive lydbok-avtaler', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(undertittelLydbok);
    cy += 30;

    if (lydbok.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen aktive lydbok-avtaler.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      cy += 36;
    } else {
      // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt tittel +
      // meta i stedet for fast 110px. Tittelen (tjeneste · boktittel) wrapper
      // på width-240 og dyttet før meta-linjen utenfor raden. radY akkumuleres
      // manuelt; bg lages etter måling.
      const RAD_GAP = 8;
      const PAD = 14;
      let radY = cy;
      for (let i = 0; i < lydbok.length; i += 1) {
        const a = lydbok[i];
        const dagerIgjen = Math.max(0, a.ferdigDag - gameState.snapshot.dag);
        const dagerGaatt = Math.min(
          Math.max(0, gameState.snapshot.dag - a.startDag),
          a.ferdigDag - a.startDag,
        );
        const totalRoyalty = a.dagligRoyalty * dagerGaatt;
        const totalInntekt = a.engangsBelop + totalRoyalty;

        const tittelT = this.scene.add
          .text(20, 0, `♫ ${a.tjenesteNavn} · ${a.bokTittel}`, {
            fontFamily: FONTS.display,
            fontSize: fontStr(18),
            color: '#3d5a4d',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 0);

        const meta = this.scene.add
          .text(
            20,
            0,
            `${dagerIgjen} dager igjen · ${a.dagligRoyalty.toLocaleString('nb-NO')} kr/dag · totalt ${totalInntekt.toLocaleString('nb-NO')} kr`,
            {
              fontFamily: FONTS.body,
              fontSize: fontStr(13),
              color: '#3a2e22',
              fontStyle: 'italic',
            },
          )
          .setOrigin(0, 0);

        tittelT.setPosition(20, radY + 16);
        const radH = Math.max(110, 16 + tittelT.height + 8 + meta.height + PAD);
        meta.setPosition(20, radY + radH - PAD - meta.height);

        // Runde 44: rounded lydbok-rad (8px).
        const bg = lagRundetRekt(
          this.scene,
          0,
          radY,
          width,
          radH,
          0x3d5a4d,
          0.14,
          8,
        );
        layer.add(bg);

        const venstre = lagRundetRekt(
          this.scene,
          0,
          radY,
          6,
          radH,
          0x3d5a4d,
          1,
          2,
        );
        layer.add(venstre);

        layer.add(tittelT);
        layer.add(meta);

        radY += radH + RAD_GAP;
      }
      cy = radY - RAD_GAP + 16;
    }

    const undertittelFilm = this.scene.add
      .text(0, cy, 'Film-rettigheter', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(undertittelFilm);
    cy += 30;

    if (film.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen film-rettigheter solgt ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      cy += 36;
    } else {
      // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt tittel +
      // status-tekst (begge kan wrappe på width-240) i stedet for fast 110px.
      // radY akkumuleres manuelt; bg lages etter måling.
      const RAD_GAP = 8;
      const PAD = 14;
      let radY = cy;
      for (let i = 0; i < film.length; i += 1) {
        const r = film[i];
        const statusTekst = filmStatusTekst(r, gameState.snapshot.dag);

        const tittelT = this.scene.add
          .text(20, 0, `✷ ${r.studioNavn} · ${r.bokTittel}`, {
            fontFamily: FONTS.display,
            fontSize: fontStr(18),
            color: '#4a3a5e',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 0);

        const meta = this.scene.add
          .text(20, 0, statusTekst, {
            fontFamily: FONTS.body,
            fontSize: fontStr(13),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 0);

        tittelT.setPosition(20, radY + 16);
        const radH = Math.max(110, 16 + tittelT.height + 8 + meta.height + PAD);
        meta.setPosition(20, radY + radH - PAD - meta.height);

        // Runde 44: rounded film-rad (8px).
        const bg = lagRundetRekt(
          this.scene,
          0,
          radY,
          width,
          radH,
          0x4a3a5e,
          0.14,
          8,
        );
        layer.add(bg);

        const venstre = lagRundetRekt(
          this.scene,
          0,
          radY,
          6,
          radH,
          0x4a3a5e,
          1,
          2,
        );
        layer.add(venstre);

        layer.add(tittelT);
        layer.add(meta);

        radY += radH + RAD_GAP;
      }
      cy = radY - RAD_GAP;
    }

    return cy;
  }

  private renderCalidrisAvkortninger(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const avkortninger = gameState.calidrisAvkortninger;
    if (avkortninger.length === 0) return y;

    const tittel = this.scene.add
      .text(0, y, 'CALIDRIS-AVKORTNINGER', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);
    let cy = y + 40;

    const undertittel = this.scene.add
      .text(0, cy, 'Calidris Forlag', {
        fontFamily: FONTS.display,
        fontSize: fontStr(18),
        fontStyle: 'italic',
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    layer.add(undertittel);
    cy += 32;

    const alleBoker = [
      ...gameState.utgitteBoker,
      ...gameState.iProduksjon.filter((b) => b.status === 'utgitt'),
    ];
    const tittelKart = new Map<string, string>();
    for (const b of alleBoker) tittelKart.set(b.isbn, b.title);

    // Layout-primitiv (2026-06-04): rad-høyden regnes fra målt boktittel +
    // meta i stedet for fast 84px. Tittelen wrapper på width-200 og kolliderte
    // før med den bunn-forankrede meta-linjen. radY akkumuleres manuelt; bg
    // lages etter måling.
    const RAD_GAP = 8;
    const PAD = 12;
    let radY = cy;
    for (let i = 0; i < avkortninger.length; i += 1) {
      const a = avkortninger[i];

      const bokTittel = tittelKart.get(a.isbn) ?? a.isbn;
      const tittelT = this.scene.add
        .text(20, 0, bokTittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(18),
          color: '#8b5e3c',
          wordWrap: { width: width - 200 },
        })
        .setOrigin(0, 0);

      const meta = this.scene.add
        .text(20, 0, 'tatt av Calidris Forlag', {
          fontFamily: FONTS.body,
          fontSize: fontStr(13),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);

      tittelT.setPosition(20, radY + 14);
      const radH = Math.max(84, 14 + tittelT.height + 6 + meta.height + PAD);
      meta.setPosition(20, radY + radH - PAD - meta.height);

      // Runde 44: rounded Calidris-avkortning-rad (8px).
      const bg = lagRundetRekt(
        this.scene,
        0,
        radY,
        width,
        radH,
        0xb8860b,
        0.14,
        8,
      );
      layer.add(bg);

      const venstre = lagRundetRekt(
        this.scene,
        0,
        radY,
        6,
        radH,
        0xb8860b,
        1,
        2,
      );
      layer.add(venstre);

      layer.add(tittelT);
      layer.add(meta);

      const prosentT = this.scene.add
        .text(width - 16, radY + radH / 2, `${a.prosentTilCalidris} %`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(24),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(prosentT);

      radY += radH + RAD_GAP;
    }

    return radY - RAD_GAP;
  }

  private renderPrisFilterPiller(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const piller: Array<{ id: PrisArkivFilter; label: string }> = [
      { id: 'alle', label: 'Alle priser' },
      { id: 'brage', label: 'Brage' },
      { id: 'bokhandlerprisen', label: 'Bokhandler' },
      { id: 'ungdommens', label: 'Ungdommens' },
      { id: 'p2-lytternes', label: 'P2-lytternes' },
    ];

    const pilleH = 48;
    const padX = 16;
    let cx = 0;
    let cy = y;
    const gap = 10;

    for (const p of piller) {
      const aktiv = this.prisFilter === p.id;
      const txt = this.scene.add
        .text(0, 0, p.label, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: aktiv ? '#ffffff' : '#3a2e22',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const w = txt.width + padX * 2;

      if (cx + w > width) {
        cx = 0;
        cy += pilleH + gap;
      }

      const bg = this.scene.add
        .rectangle(cx, cy, w, pilleH, aktiv ? COLORS.wine : 0xd9a14b, aktiv ? 1 : 0.35)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      bg.on('pointerup', () => {
        this.prisFilter = p.id;
        this.render();
      });
      layer.add(bg);

      txt.setPosition(cx + padX, cy + (pilleH - txt.height) / 2);
      layer.add(txt);

      cx += w + gap;
    }
    return cy + pilleH;
  }

  private kompakt(n: number): string {
    const neg = n < 0;
    const abs = Math.abs(n);
    let str: string;
    if (abs >= 1_000_000) str = `${(abs / 1_000_000).toFixed(1)}M`;
    else if (abs >= 10_000) str = `${Math.round(abs / 1_000)}k`;
    else str = abs.toLocaleString('nb-NO');
    return neg ? `−${str}` : str;
  }
}

const SESONG_GRENSER: ReadonlyArray<{ slutt: number; sesong: Sesong }> = [
  { slutt: 20, sesong: 'vaar' },
  { slutt: 60, sesong: 'sommer' },
  { slutt: 80, sesong: 'bokhost' },
  { slutt: 100, sesong: 'jul' },
  { slutt: DAGER_PER_AAR, sesong: 'vinter' },
];

function nesteSesong(dag: number): Sesong {
  const d = dagIAaret(dag);
  for (const grense of SESONG_GRENSER) {
    if (d <= grense.slutt) {
      const idx = SESONG_GRENSER.indexOf(grense);
      const neste = SESONG_GRENSER[(idx + 1) % SESONG_GRENSER.length];
      return neste.sesong;
    }
  }
  return 'vaar';
}

function filmStatusTekst(r: FilmRettighet, dag: number): string {
  if (r.produseresStatus === 'lansert') {
    const dagerSiden = r.produseresFerdigDag !== null ? dag - r.produseresFerdigDag : 0;
    return `lansert · ${dagerSiden} dager siden · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  if (r.produseresStatus === 'ikke_produsert') {
    return `ikke produsert · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  if (r.produseresStatus === 'produseres' && r.produseresFerdigDag !== null) {
    const igjen = Math.max(0, r.produseresFerdigDag - dag);
    return `produseres · ${igjen} dager til avgjørelse · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  return `venter · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
}

function dagerTilSesongSkift(dag: number): number {
  const d = dagIAaret(dag);
  for (const grense of SESONG_GRENSER) {
    if (d <= grense.slutt) return grense.slutt - d + 1;
  }
  return DAGER_PER_AAR - d + 1;
}

function beregnStatSnitt(stats: import('@/data/types').AuthorStats): number {
  return (
    (stats.dialog +
      stats.plot +
      stats.karakter +
      stats.spraak +
      stats.voice +
      stats.autentisitet +
      stats.disiplin) /
    7
  );
}
